/**
 * Standalone frame for the customer sign-in, sign-up and onboarding pages: no top bar, drawer, search, workspace
 * switcher or notifications - just the lockup, the page and the EN/AR switch (RTL follows the locale).
 * Editorial Brand System 2026: the fullest composition in the portal - fine grid field, off-canvas lavender discs
 * and rings (decorative, aria-hidden), an oversized headline with one serif pivot, and the form on white.
 */
import { useEffect, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router';
import { BrandRoot, EditorialArt, MicroLabel, SkipLink } from '@qm/ui';
import { assets } from '@qm/brand';
import { AppProvider, useApp, useOptionalApp } from '../lib/app-context';
import { applyDocumentPrefs, resolveMotion, resolveTheme } from '../lib/prefs';
import { LanguageToggle, uiVersion, useMedia } from './Shell';

export function AuthLayout() {
  const existing = useOptionalApp();
  const frame = <AuthFrame><Outlet /></AuthFrame>;
  return existing ? frame : <AppProvider>{frame}</AppProvider>;
}

export function AuthFrame({ children }: { children: ReactNode }) {
  const { t, locale, dir, ui } = useApp();
  const systemDark = useMedia('(prefers-color-scheme: dark)');
  const systemReduce = useMedia('(prefers-reduced-motion: reduce)');
  const theme = resolveTheme(ui.theme, systemDark);
  const motion = resolveMotion(ui.motion, systemReduce);
  useEffect(() => {
    if (typeof document !== 'undefined') applyDocumentPrefs(document.documentElement, { locale, theme, motion });
  }, [locale, theme, motion]);
  const lockup = theme === 'dark' ? assets.lockupOnBlack : assets.lockup;
  const site = locale === 'ar' ? 'https://quixomarketing.com/ar' : 'https://quixomarketing.com';
  const signup = /^\/(signup|sign-up)(\/|$)/.test(useLocation().pathname);
  return (
    <BrandRoot dir={dir} lang={locale}>
      <div className="qm-auth" data-layout="auth" data-ui={uiVersion('customer')}>
        <SkipLink>{t('shell.skipToContent')}</SkipLink>
        <header className="qm-auth__bar">
          <a href={site} className="qm-auth__brand" aria-label={t('shell.auth.brandHome')}>
            <img className="qm-auth__lockup" src={lockup} alt={t('common.brand')} width={168} height={28} />
          </a>
          <LanguageToggle />
        </header>
        <div className="qm-auth__layout">
          <div className="qm-auth__hero">
            <EditorialArt variant={signup ? 'signup' : 'signin'} />
            <div className="qm-auth__hero-text">
              <MicroLabel>{t('shell.auth.eyebrow')}</MicroLabel>
              <p className="qm-auth__headline">
                {signup ? t('shell.auth.signupTitle') : t('shell.auth.heroTitle')} <span className="qm-pivot">{signup ? t('shell.auth.signupPivot') : t('shell.auth.heroPivot')}</span>
              </p>
            </div>
            <MicroLabel className="qm-auth__note">{t('shell.auth.note')}</MicroLabel>
          </div>
          <main id="main" className="qm-auth__main" tabIndex={-1}>{children}</main>
        </div>
      </div>
    </BrandRoot>
  );
}
